/**
 * PlaytestDialogs — spec-aligned modal dialogs for the new playtest engine.
 *
 *   1. PendingPlayDialog        — confirm a queued play (cost, "Not enough stamina")
 *   2. CounterActivateDialog    — "Activate Counter?" amber modal
 *   3. CounterActivatedDialog   — "Discard & Draw" / "Keep Card" two-button
 *   4. ReactionWindowBar        — violet bottom bar during free-reaction window
 */
import type { Card } from "@/types/card";
import type { GameState, PendingPlay } from "@/types/game";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { BoardCard } from "./BoardCard";
import { previewEffectiveCost } from "@/game/playtestEngine";
import type { AdMode } from "@/types/game";

// ─── Pending Play (Confirm) ────────────────────────────────────────────────

interface PendingPlayDialogProps {
  pp: PendingPlay;
  game: GameState;
  cardMap: Map<string, Card>;
  onConfirm: () => void;
  onCancel: () => void;
}

export function PendingPlayDialog({
  pp,
  game,
  cardMap,
  onConfirm,
  onCancel,
}: PendingPlayDialogProps) {
  const player = game.players[pp.playerNum];

  // Resolve the card backing this pending play. Hand-played A/D plays carry
  // a `playCard` instance; equipment and signature plays only carry a
  // `slotIdx` pointing at the player's equipSlots / sigMoveSlots, so we
  // resolve those here too — otherwise previewEffectiveCost falls through
  // to the raw `pp.cost` and stance/buff reductions get ignored (e.g.
  // Soulreaver heavy-attack while Zeus' Wrathbrand is in play).
  let cardDbId: string | undefined = pp.playCard?.cardId;
  if (!cardDbId && pp.slotIdx != null) {
    if (pp.type === "equip-attack" || pp.type === "equip-defend") {
      cardDbId = player.equipSlots[pp.slotIdx]?.cardId ?? undefined;
    } else if (
      pp.type === "sig-attack" ||
      pp.type === "sig-defend" ||
      pp.type === "sig-utility" ||
      // Quick Learner copies live in the sig slot but resolve as their actual
      // card type. The dialog reads the picked card from the sig slot here so
      // previewEffectiveCost can apply A/D / Utility / Stance / Equipment
      // cost rules correctly.
      pp.type === "qlsig-ad-attack" ||
      pp.type === "qlsig-ad-defend" ||
      pp.type === "qlsig-utility" ||
      pp.type === "qlsig-stance" ||
      pp.type === "qlsig-equip-attack" ||
      pp.type === "qlsig-equip-defend"
    ) {
      cardDbId = player.sigMoveSlots[pp.slotIdx]?.cardId ?? undefined;
    }
  }
  const card = cardDbId ? cardMap.get(cardDbId) : null;

  // Derive the effective stamina cost factoring in stance reductions
  // (Committed Form / Zeus' Wrathbrand) and fired buff cost-reductions
  // (Battle Adrenaline, Wound-Bind, etc.) so the legality check matches
  // the engine's check at play-time. pp.cost is the printed/raw cost —
  // it does not account for these reductions.
  const ppMode: AdMode | null =
    pp.type === "ad-attack" || pp.type === "equip-attack" || pp.type === "sig-attack" ||
    pp.type === "qlsig-ad-attack" || pp.type === "qlsig-equip-attack"
      ? "attack"
      : pp.type === "ad-defend" || pp.type === "equip-defend" || pp.type === "sig-defend" ||
        pp.type === "qlsig-ad-defend" || pp.type === "qlsig-equip-defend"
      ? "defend"
      : null;
  const cost = card
    ? previewEffectiveCost(player, card, ppMode, cardMap, game.round)
    : pp.cost ?? 0;
  const insufficient = cost > player.stamina;

  const titleByType: Record<PendingPlay["type"], string> = {
    "ad-attack": "Attack",
    "ad-defend": "Defend",
    "equip-attack": "Attack with Equipment",
    "equip-defend": "Defend with Equipment",
    "sig-attack": "Signature Move (Attack)",
    "sig-defend": "Signature Move (Defend)",
    "sig-utility": "Signature Move (Utility)",
    "play-stance": "Play Stance",
    "play-utility": "Play Utility",
    "armor-block": "Armor Block",
    "take-damage": "Take Damage",
    "token-attack": "Token Attack",
    "token-defend": "Token Defend",
    "expend-focus": "Expend Focus",
    "qlsig-ad-attack": "Quick Learner — Attack",
    "qlsig-ad-defend": "Quick Learner — Defend",
    "qlsig-utility": "Quick Learner — Utility",
    "qlsig-stance": "Quick Learner — Stance",
    "qlsig-equip-attack": "Quick Learner — Attack (Equipment)",
    "qlsig-equip-defend": "Quick Learner — Defend (Equipment)",
  };

  return (
    <Modal open={true} onOpenChange={(o) => !o && onCancel()} title={`Confirm: ${titleByType[pp.type]}`}>
      <div className="space-y-3">
        {card && (
          <div className="flex justify-center">
            <BoardCard card={card} size="hand" />
          </div>
        )}
        {pp.info && <p className="text-sm text-[#94a3b8]">{pp.info}</p>}
        {pp.type !== "take-damage" && (
          <div className="flex items-center justify-between rounded border border-[#2a2f3a] bg-[#0e1014] px-3 py-2 text-sm">
            <span className="text-[#94a3b8]">Stamina cost</span>
            <span className="font-mono">
              {cost} / {player.stamina}
              {insufficient && (
                <span className="ml-2 text-red-400">Not enough stamina!</span>
              )}
            </span>
          </div>
        )}
        {pp.type === "take-damage" && (
          <p className="text-sm text-red-300">You will take damage from this attack.</p>
        )}
        <div className="flex justify-end gap-2 border-t border-[#2a2f3a] pt-3">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={insufficient}>
            Confirm
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Counter Activate (amber) ──────────────────────────────────────────────

interface CounterActivateDialogProps {
  card: Card;
  buffSummary?: string[];
  onConfirm: () => void;
  onCancel: () => void;
}

export function CounterActivateDialog({
  card,
  buffSummary,
  onConfirm,
  onCancel,
}: CounterActivateDialogProps) {
  return (
    <Modal open={true} onOpenChange={(o) => !o && onCancel()} title="Activate Counter?">
      <div className="space-y-3">
        <div className="rounded border border-amber-soft/40 bg-amber-500/10 p-3 text-sm">
          <p className="font-display text-base font-semibold text-amber-soft">{card.name}</p>
          {buffSummary && buffSummary.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-[#e2e8f0]">
              {buffSummary.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-[#2a2f3a] pt-3">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={onConfirm}>Confirm</Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Counter Activated (Discard&Draw / Keep) ───────────────────────────────

interface CounterActivatedDialogProps {
  card: Card;
  buffSummary?: string[];
  onDiscardAndDraw: () => void;
  onKeep: () => void;
}

export function CounterActivatedDialog({
  card,
  buffSummary,
  onDiscardAndDraw,
  onKeep,
}: CounterActivatedDialogProps) {
  return (
    <Modal open={true} onOpenChange={() => {}} title="Counter Activated">
      <div className="space-y-3">
        <p className="text-sm text-[#94a3b8]">
          <b className="text-amber-soft">{card.name}</b> has been activated. Choose:
        </p>
        {buffSummary && buffSummary.length > 0 && (
          <ul className="rounded border border-[#2a2f3a] bg-[#0e1014] p-2 text-xs text-[#94a3b8]">
            {buffSummary.map((b, i) => (
              <li key={i}>• {b}</li>
            ))}
          </ul>
        )}
        <div className="flex justify-end gap-2 border-t border-[#2a2f3a] pt-3">
          <Button variant="secondary" onClick={onDiscardAndDraw}>
            Discard &amp; Draw
          </Button>
          <Button onClick={onKeep}>Keep Card</Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Reaction Window (violet bottom bar) ───────────────────────────────────

interface ReactionWindowBarProps {
  message: string;
  onPass: () => void;
}

export function ReactionWindowBar({ message, onPass }: ReactionWindowBarProps) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-30 flex items-center justify-between gap-3 border-t-2 border-violet-500 bg-violet-950/80 px-4 py-2 text-sm backdrop-blur">
      <span className="font-semibold text-violet-200">⚡ {message}</span>
      <Button variant="secondary" size="sm" onClick={onPass}>
        Pass — No Reaction
      </Button>
    </div>
  );
}
